<template>
  <div v-if="shown" class="bot-down" role="alert">
    <button class="bd-dismiss" @click="dismiss" title="Hide this until something changes" aria-label="Hide this until something changes">✕</button>
    <b>🆘 Help, wiznaibot is down! Please contact Luxor.</b>
    <span class="bd-why">{{ why }}</span>
    <a v-if="!onBotPage" class="bd-link" href="?wiznaibot">Wiznaibot page →</a>
  </div>
</template>

<script>
// The "wiznaibot is down" banner, on the bracket page and the Wiznaibot page. It asks the
// betting bot's health endpoint (through the wiznaibot-health Function) every 5 minutes,
// and lib/botHealth.js decides. The point is for viewers to see it: on 2026-09-25 the bot
// lost Twitch chat and nobody noticed for three days. Add `?botdown` to the URL to see it.
import { botDownReason } from '../lib/botHealth.js';

const POLL_MS = 5 * 60 * 1000;
const RECHECK_MS = 60 * 1000;  // after the first "didn't answer", in case it was a blip
const DISMISS_KEY = 'wiznaibus.botDownDismissed';

export default {
  name: 'BotDownBanner',
  props: {
    // The live tournament's LastMod, so it can tell whether the bot is missing matches.
    // Leave it null anywhere else.
    lastMod: { type: [String, Number], default: null },
  },
  data() {
    return { status: 0, health: null, misses: 0, reason: null, test: false, dismissed: null, timer: null };
  },
  computed: {
    onBotPage() { return new URLSearchParams(location.search).has('wiznaibot'); },
    // A new outage, or a new stage of this one, shows again after a dismissal.
    key() { return this.reason ? this.reason.kind + ':' + (this.reason.since || '') : ''; },
    shown() { return !!this.reason && this.dismissed !== this.key; },
    why() {
      const r = this.reason, t = r && r.since ? this.when(r.since) : '';
      switch (r && r.kind) {
        case 'unreachable': return "Its server isn't answering.";
        case 'offline': return t ? `It's been disconnected from Twitch chat since ${t}.` : "It can't connect to Twitch chat.";
        case 'quiet': return `It hasn't heard anything from Twitch chat since ${t}.`;
        case 'missing': return `Matches are being played, but it hasn't seen betting open since ${t || 'it started'}.`;
        case 'test': return 'This is only a test: the URL has ?botdown in it.';
        default: return '';
      }
    },
  },
  watch: {
    lastMod() { this.judge(); },  // a match just finished
  },
  created() {
    try { this.dismissed = sessionStorage.getItem(DISMISS_KEY); } catch { /* storage blocked */ }
    if (new URLSearchParams(location.search).has('botdown')) {
      this.test = true;
      this.reason = { kind: 'test', since: null };
      return;
    }
    this.timer = setTimeout(this.check, 3000);  // let the page's own loads go first
  },
  beforeUnmount() { clearTimeout(this.timer); },
  methods: {
    async check() {
      let status = 0, health = null;
      try {
        const res = await fetch('/.netlify/functions/wiznaibot-health');
        status = res.status;
        health = await res.json().catch(() => null);
      } catch { /* offline, or no Functions (the local dev server) */ }
      this.status = status;
      this.health = health;
      const missed = status === 502 || status === 504;
      this.misses = missed ? this.misses + 1 : 0;
      this.judge();
      this.timer = setTimeout(this.check, this.misses === 1 ? RECHECK_MS : POLL_MS);
    },
    judge() {
      if (this.test) return;
      const r = botDownReason({ status: this.status, health: this.health, lastMod: this.lastMod });
      this.reason = r && r.kind === 'unreachable' && this.misses < 2 ? null : r;
    },
    when(ms) {
      const d = new Date(ms), time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      return d.toDateString() === new Date().toDateString() ? time : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ', ' + time;
    },
    dismiss() {
      this.dismissed = this.key;
      try { sessionStorage.setItem(DISMISS_KEY, this.key); } catch { /* storage blocked */ }
    },
  },
};
</script>

<style scoped>
.bot-down {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: baseline;
  gap: 4px 10px;
  padding: 10px 40px;
  background: #b3261e;
  color: #fff;
  border-bottom: 2px solid #fff;
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 15px;
  line-height: 1.4;
}
.bd-why { font-size: 13px; opacity: 0.9; }
.bd-link { font-size: 13px; color: #ffe08a; text-decoration: none; white-space: nowrap; }
.bd-link:hover { text-decoration: underline; }
.bd-dismiss {
  position: absolute;
  top: 6px;
  right: 8px;
  background: none;
  border: 0;
  color: #fff;
  opacity: 0.75;
  font-size: 15px;
  cursor: pointer;
  padding: 4px 6px;
}
.bd-dismiss:hover { opacity: 1; }
</style>
